/**
 * @file PickMarkPanel.tsx
 * @description Правая панель «Отметить позицию» на экране задания
 * (phase19.24, решение пользователя 5 «как в приёмке», 2026-09-20; общая
 * карточка `DocumentAside` — этап 1, шаг 1).
 *
 * Раскладка повторяет приёмку: слева позиции, справа одно место ввода — поле
 * скана/артикула и «Подбор» в одной строке, ниже количество и «Отметить».
 * Поля скана над таблицей больше нет: два поля на одном экране забирали друг
 * у друга фокус, и скан улетал «в никуда».
 *
 * Панель живёт ТОЛЬКО у живого своего задания (правило §5.2 «закрытый
 * документ — без панели»). У закрытого и чужого поле поиска рисует
 * `PickingItemsSearch` над списком — отмечать там нечего.
 *
 * Карточку рисует `DocumentAside` с `chrome="lg"`: на `<lg` панель
 * разворачивается в обычное поле сверху, на `lg` — в панель 320/360px. Две
 * копии поля с одним `scanRef` означали бы два активных input и потерянный
 * скан.
 *
 * Выбор строки через «Подбор» уводит курсор в «Количество» — как выбор
 * фасовки в приёмке. После скана курсор остаётся в поле: сканер отмечает
 * строку сам, и серия сканов подряд не должна прерываться.
 */

import { ListPlus } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { PickItemPickerDialog } from "./PickItemPickerDialog";
import { PickMarkForm } from "./PickMarkForm";
import { PickMarkProgress } from "./PickMarkProgress";
import { usePickMarkForm } from "./usePickMarkForm";
import { Button } from "../../ui/button";
import { DocumentAside } from "../../ui/DocumentAside";
import { ScanField, type ScanFieldHandle } from "../../ui/ScanField";
import { WmsFieldLabel } from "../WmsFieldLabel";
import type { PickingTaskItemDto } from "../../../lib/api-picking";

interface Props {
  /** Все строки задания — для «Подбора» и проверки плана. */
  items: PickingTaskItemDto[];
  busy: boolean;
  scanRef: React.RefObject<ScanFieldHandle | null>;
  /** Ввод завершён: код и признак «пришло со сканера». */
  onSubmitScan: (code: string, fromScanner: boolean) => void;
  onQueryChange: (value: string) => void;
  /** Отметить строку: итоговое собранное количество. */
  onMark: (itemId: string, qtyPicked: number) => void;
  /** Строка последнего скана — панель показывает её же. */
  scannedItemId: string | null;
  picked: number;
  total: number;
  shortageCount: number;
  /** phase19.22 (W14-9) — отметка ждёт скана ячейки: адрес строки. */
  binRequest?: string | null;
}

export function PickMarkPanel({
  items,
  busy,
  scanRef,
  onSubmitScan,
  onQueryChange,
  onMark,
  scannedItemId,
  picked,
  total,
  shortageCount,
  binRequest = null,
}: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const qtyRef = useRef<HTMLInputElement>(null);

  // Скан отметил строку — панель показывает её же: кладовщик видит, с чем
  // работает, и может добить остаток руками, не ища позицию заново.
  useEffect(() => {
    if (scannedItemId) setSelectedId(scannedItemId);
  }, [scannedItemId]);

  const selected = items.find((i) => i.id === selectedId) ?? null;
  const form = usePickMarkForm(selected, onMark);

  /** Ручной выбор строки: дальше кладовщик вводит количество. */
  function pickItem(item: PickingTaskItemDto) {
    setSelectedId(item.id);
    setPickerOpen(false);
    qtyRef.current?.focus();
  }

  return (
    <DocumentAside
      title="Отметить позицию"
      // Карточка только на `lg`: на телефоне панель — это одно поле скана над
      // списком, рамка вокруг него была бы пустой коробкой.
      chrome="lg"
      titleClassName="hidden lg:flex"
      // На `<lg` слот кнопки не рендерим совсем: скрытая кнопка оставляла
      // пустую обёртку и лишний зазор под полем (замечание ревью 2026-09-20).
      footerClassName="hidden lg:flex"
      footer={
        <>
          <Button onClick={form.mark} disabled={busy}>
            Отметить
          </Button>
          {form.error ? (
            <p className="text-xs text-destructive">{form.error}</p>
          ) : null}
        </>
      }
    >
      <WmsFieldLabel label="Фасовка (артикул или скан)" className="hidden lg:block">
        {/* Одна раскладка первого поля во всех четырёх документах: поле и
            «Подбор» в одной строке. preventDefault на кнопке обязателен —
            внутри `<label>` браузер иначе трактует клик как клик по подписи и
            уводит фокус в поле, а открывшийся диалог его терял. */}
        <div className="flex min-w-0 gap-2">
          <div className="min-w-0 flex-1">
            <ScanField
              ref={scanRef}
              mode="scanAndSearch"
              busy={busy}
              helperText="Скан или точный артикул отмечает позицию, другой текст — поиск"
              onQueryChange={onQueryChange}
              onSubmit={(code, meta) => onSubmitScan(code, meta.fromScanner)}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            className="shrink-0 self-start"
            onClick={(e) => {
              e.preventDefault();
              setPickerOpen(true);
            }}
            disabled={busy}
            title="Выбрать позицию из состава задания"
          >
            <ListPlus className="size-4" />
            <span className="ml-1.5">Подбор</span>
          </Button>
        </div>
      </WmsFieldLabel>
      {binRequest !== null ? (
        <p role="status" className="text-sm font-medium text-warning">
          {binRequest ? `Отсканируйте ячейку ${binRequest}` : "Отсканируйте ячейку позиции"} — отметка
          повторится сама.
        </p>
      ) : null}

      <div className="hidden flex-col gap-3 lg:flex">
        <PickMarkForm item={selected} state={form} qtyRef={qtyRef} />
        <PickMarkProgress picked={picked} total={total} shortageCount={shortageCount} />
      </div>

      <PickItemPickerDialog
        open={pickerOpen}
        items={items}
        // Фокус после выбора ведёт сам диалог — один порядок с приёмкой,
        // списанием и перемещением (`usePickerFinalFocus`).
        nextFieldRef={qtyRef}
        onSelect={pickItem}
        onClose={() => {
          setPickerOpen(false);
          scanRef.current?.focus();
        }}
      />
    </DocumentAside>
  );
}
